import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { FetchStatusBadge } from "../../components/badges";
import { ErrorAlert } from "../../components/ErrorAlert";
import { dashIfEmpty, formatDate } from "../../lib/format";
import { stockxProductId } from "../../lib/stockxPayload";
import { useTRPC } from "../../lib/trpc";

// The list procedure takes no status filter, so one page of rows is filtered in memory.
const PAGE_SIZE = 100;

const FILTERS = ["ALL", "PENDING", "PARTIAL", "COMPLETE", "FAILED"] as const;

const COLUMNS = ["Status", "Product key", "Vendor", "Requested by", "Updated", "Error"] as const;

/** A fetch is keyed by its vendor and the vendor's own product key; there is no id column. */
function rowKey(row: { vendor: string; externalId: string }) {
  return `${row.vendor}:${row.externalId}`;
}

/**
 * The fetch row as this page reads it. Spelled out rather than inferred from
 * the router, and assigned through `unknown`, because the payload's recursive
 * `JsonObject` makes tsc give up on either comparison ("excessively deep").
 */
type QueueRow = {
  vendor: string;
  externalId: string;
  status: string;
  requestedBy: string;
  errorCode: string | null;
  errorMessage: string | null;
  updatedAt: Date | string;
  payload: unknown;
};

/** The deep-fetch queue: recent product_fetch rows with their status and any error. */
export function StockxFetchQueuePage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("ALL");

  const fetches = useQuery(
    trpc.platform.fulfillment.productFetch.list.queryOptions({ limit: PAGE_SIZE }),
  );
  const all = (fetches.data?.fetches ?? []) as unknown as QueueRow[];
  const rows = filter === "ALL" ? all : all.filter((row) => row.status === filter);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Fetch queue</h1>
        <p className="text-sm text-muted-foreground">
          The {PAGE_SIZE} most recent StockX deep fetches, newest first.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((option) => (
          <Button
            key={option}
            type="button"
            size="sm"
            variant={option === filter ? "default" : "ghost"}
            onClick={() => setFilter(option)}
          >
            {option}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={fetches.isFetching}
          onClick={() => void fetches.refetch()}
        >
          {fetches.isFetching ? "Loading…" : "Reload"}
        </Button>
      </div>

      {fetches.error && <ErrorAlert message={fetches.error.message} />}

      <Card>
        <CardHeader>
          <CardTitle>
            {rows.length} of {all.length} loaded
          </CardTitle>
        </CardHeader>
        <CardContent>
          {fetches.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No fetches match this filter.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {COLUMNS.map((column) => (
                      <TableHead key={column}>{column}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    // A row whose fetch never landed carries no StockX id, so it has nowhere to go.
                    const productId = stockxProductId(row.payload);
                    return (
                      <TableRow
                        key={rowKey(row)}
                        className={productId ? "cursor-pointer" : undefined}
                        onClick={
                          productId
                            ? () =>
                                void navigate({
                                  to: "/fulfillment/stockx/$productId",
                                  params: { productId },
                                })
                            : undefined
                        }
                      >
                        <TableCell>
                          <FetchStatusBadge status={row.status} />
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {productId ? (
                            <Link
                              className="underline underline-offset-4"
                              to="/fulfillment/stockx/$productId"
                              params={{ productId }}
                              // Without this the row's handler also navigates, so a cmd-click
                              // opens the new tab and replaces this page at the same time.
                              onClick={(event) => event.stopPropagation()}
                            >
                              {row.externalId}
                            </Link>
                          ) : (
                            row.externalId
                          )}
                        </TableCell>
                        <TableCell>{row.vendor}</TableCell>
                        <TableCell>{dashIfEmpty(row.requestedBy)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(row.updatedAt)}
                        </TableCell>
                        <TableCell>
                          {row.errorCode
                            ? `${row.errorCode}: ${dashIfEmpty(row.errorMessage)}`
                            : dashIfEmpty(null)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
